import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback } from 'react';
import { ArrowLeft, Compass, MapPin, Info, Lock, Users, ChevronRight, Home } from 'lucide-react';
import { getRealmById } from '../config/worldData';
import { getMidgardRegions, getMidgardSettlements } from '../config/midgardData';
import { usePresence, getSettlementPresence, formatPresenceText } from '../hooks/usePresence';
import GatewayArrival from '../components/world/GatewayArrival';

/**
 * MidgardRegionalMap - The detailed regional map of Midgard
 * 
 * Uses the new detailed artwork map with responsive hotspots.
 * Hotspots use normalized coordinates (percentages) to stay aligned
 * with the artwork across all screen sizes.
 */
export default function MidgardRegionalMap() {
  const navigate = useNavigate();
  const [hoveredSettlement, setHoveredSettlement] = useState(null);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const realm = getRealmById('midgard');
  const regions = getMidgardRegions();
  const settlements = getMidgardSettlements();
  
  // Fetch real-time presence data
  const { presence, loading: presenceLoading } = usePresence(true);
  
  // Get presence count for selected settlement
  const selectedPresenceCount = useMemo(() => {
    if (!selectedSettlement) return 0;
    return getSettlementPresence(presence, selectedSettlement.id);
  }, [selectedSettlement, presence]);

  // Settlement hotspot positions - normalized percentages on the artwork
  // Precisely calibrated to match the map artwork across desktop/mobile/tablet
  // IDs match midgardData.js: thornhaven, frostholm, stonewatch, ravenmoor, mirefen
  const settlementPositions = useMemo(() => ({
    thornhaven: { x: 42, y: 45, labelOffset: { x: 0, y: 8 } },    // Center-left, below Thornhaven label
    frostholm: { x: 60, y: 30, labelOffset: { x: 0, y: -8 } },    // Upper right, at Frostholm label
    stonewatch: { x: 75, y: 52, labelOffset: { x: 8, y: 0 } },    // Eastern coast
    ravenmoor: { x: 22, y: 58, labelOffset: { x: -8, y: 0 } },    // Western forests  
    mirefen: { x: 72, y: 78, labelOffset: { x: 0, y: 8 } },       // Southern marshes, at Mirefen label
  }), []);

  const handleBack = () => {
    navigate('/world');
  };

  const handleEnterSettlement = useCallback(() => {
    if (selectedSettlement && selectedSettlement.unlocked) {
      navigate(`/world/midgard/settlement/${selectedSettlement.id}`);
    }
  }, [selectedSettlement, navigate]);

  const handleSettlementClick = useCallback((settlement, e) => {
    e.stopPropagation();
    setSelectedSettlement(settlement);
  }, []);

  const handleMapClick = useCallback(() => {
    setSelectedSettlement(null);
  }, []);

  return (
    <div 
      className="mg-stage"
      style={{ backgroundColor: '#1a1a1a' }}
      data-testid="midgard-regional-map"
      onClick={handleMapClick}
    >
      <GatewayArrival />
      {/* Background - reduced opacity to let map artwork show through */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-950/20 via-stone-900/30 to-yellow-950/20" />

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/home')}
            className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
            data-testid="back-to-social"
            title="Return to Social Feed"
          >
            <Home className="w-5 h-5 text-amber-100" />
          </button>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
            data-testid="back-to-world"
          >
            <ArrowLeft className="w-5 h-5 text-amber-100" />
            <span className="text-amber-100 text-sm hidden sm:inline">World Map</span>
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
          <Compass className="w-5 h-5 text-amber-500" />
          <span className="font-heading text-amber-100 text-sm uppercase tracking-wider">Midgard</span>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); setShowInfo(!showInfo); }}
          className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
          data-testid="region-info-btn"
        >
          <Info className="w-5 h-5 text-amber-100" />
        </button>
      </header>

      {/* Info Panel */}
      {showInfo && (
        <div className="absolute top-20 right-4 z-30 w-72 p-4 rounded-xl bg-black/80 backdrop-blur-md border border-amber-900/30 animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-amber-500" />
            <span className="text-xs uppercase tracking-wider text-amber-400">Realm of Humans</span>
          </div>
          <h3 className="font-heading text-amber-100 text-lg mb-2">Midgard</h3>
          <p className="text-sm text-amber-100/70 leading-relaxed mb-3">
            {realm?.description}
          </p>
          <div className="pt-3 border-t border-amber-900/30 space-y-1">
            <p className="text-xs text-amber-500/70">{regions.length} regions</p>
            <p className="text-xs text-amber-500/70">{settlements.filter(s => s.unlocked).length} settlements accessible</p>
          </div>
        </div>
      )}

      {/* Map Container with New Artwork */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pt-20 pb-8">
        <div className="relative w-full max-w-5xl max-h-[80vh] aspect-[4/3]">
          {/* Map Image */}
          <img
            src="/maps/midgard-map.png"
            alt="Map of Midgard"
            className={`w-full h-full object-contain rounded-lg transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            draggable={false}
          />
          
          {/* Loading placeholder */}
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-amber-950/50 rounded-lg">
              <div className="animate-pulse text-amber-200">Loading map...</div>
            </div>
          )}

          {/* Settlement Hotspots - Positioned using percentages */}
          {imageLoaded && settlements.map((settlement) => {
            const pos = settlementPositions[settlement.id];
            if (!pos) return null;
            
            const presenceCount = getSettlementPresence(presence, settlement.id);
            const isSelected = selectedSettlement?.id === settlement.id;
            const isHovered = hoveredSettlement === settlement.id;
            
            return (
              <button
                key={settlement.id}
                onClick={(e) => handleSettlementClick(settlement, e)}
                onMouseEnter={() => setHoveredSettlement(settlement.id)}
                onMouseLeave={() => setHoveredSettlement(null)}
                className={`
                  absolute transform -translate-x-1/2 -translate-y-1/2
                  transition-all duration-200 group
                  ${settlement.unlocked ? 'cursor-pointer' : 'cursor-not-allowed'}
                `}
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                }}
                data-testid={`settlement-marker-${settlement.id}`}
              >
                {/* Marker icon */}
                <div className={`
                  relative w-10 h-10 sm:w-12 sm:h-12 rounded-full
                  flex items-center justify-center
                  transition-all duration-200
                  ${isSelected 
                    ? 'bg-amber-500 scale-125 shadow-lg shadow-amber-500/50' 
                    : isHovered
                      ? 'bg-amber-600/90 scale-110'
                      : settlement.unlocked 
                        ? 'bg-amber-800/80 hover:bg-amber-700/90' 
                        : 'bg-gray-700/60'
                  }
                  border-2 ${isSelected ? 'border-amber-300' : 'border-amber-900/50'}
                `}>
                  {settlement.unlocked ? (
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-amber-100" />
                  ) : (
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  )}
                  
                  {/* Presence indicator */}
                  {presenceCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-black flex items-center justify-center">
                      <span className="text-[10px] font-bold text-white">{presenceCount}</span>
                    </div>
                  )}
                </div>
                
                {/* Settlement name tooltip */}
                <div className={`
                  absolute whitespace-nowrap px-2 py-1 rounded
                  bg-black/80 backdrop-blur-sm text-amber-100 text-xs
                  transition-opacity duration-200 pointer-events-none
                  ${isHovered || isSelected ? 'opacity-100' : 'opacity-0 sm:group-hover:opacity-100'}
                `}
                  style={{
                    left: `calc(50% + ${pos.labelOffset?.x || 0}px)`,
                    top: `calc(100% + ${pos.labelOffset?.y || 4}px)`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  {settlement.name}
                  {!settlement.unlocked && <span className="text-gray-400 ml-1">(Locked)</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Region Legend */}
      <div className="absolute bottom-4 left-4 z-20 p-3 rounded-xl bg-black/60 backdrop-blur-sm border border-amber-900/30 hidden sm:block">
        <h4 className="text-xs uppercase tracking-wider text-amber-500 mb-2">Regions</h4>
        <div className="space-y-1">
          {regions.slice(0, 5).map((region) => (
            <div key={region.id} className="flex items-center gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full ${region.color || 'bg-amber-600'}`} />
              <span className="text-amber-100/70">{region.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Settlement Panel */}
      {selectedSettlement && (
        <div 
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 w-full max-w-md p-4 rounded-xl bg-black/80 backdrop-blur-md border border-amber-900/30 animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 text-xs text-amber-500 uppercase tracking-wider mb-1">
                <MapPin className="w-3 h-3" />
                {selectedSettlement.type} • {selectedSettlement.region || 'Central Plains'}
              </div>
              <h3 className="font-heading text-xl text-amber-100">{selectedSettlement.name}</h3>
            </div>
            <button
              onClick={() => setSelectedSettlement(null)}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-amber-100/50 rotate-45" />
            </button>
          </div>
          
          <p className="text-sm text-amber-100/70 mb-4 line-clamp-2">
            {selectedSettlement.description}
          </p>
          
          <div className="flex items-center justify-between">
            {/* Presence info */}
            <div className="flex items-center gap-2 text-sm">
              {selectedPresenceCount > 0 ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-400">{formatPresenceText(selectedPresenceCount)}</span>
                </>
              ) : (
                <span className="text-amber-100/50">No travelers currently here</span>
              )}
            </div>
            
            {/* Enter button */}
            <button
              onClick={handleEnterSettlement}
              disabled={!selectedSettlement.unlocked}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg
                transition-all
                ${selectedSettlement.unlocked
                  ? 'bg-amber-600 hover:bg-amber-500 text-white'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {selectedSettlement.unlocked ? (
                <>
                  <span>Enter {selectedSettlement.name}</span>
                  <ChevronRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <span>Locked</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
